import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, loading, Select } from "../components/CustomComponent";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// การ์ดหลักใบเดียวของหน้า: เงินคงเหลือ + ใช้ได้อีกกี่เดือน
const heroCardStyle = {
  borderRadius: 28,
  background: "linear-gradient(135deg, #0a3f7d 0%, #0c75e6 52%, #27c6dd 100%)",
  boxShadow: "0 26px 54px rgba(9, 60, 122, 0.28)",
  position: "relative",
  overflow: "hidden",
};

const statCardStyle = {
  borderRadius: 20,
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.12)",
  boxShadow: "0 12px 26px rgba(12, 118, 229, 0.06)",
};

const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const CATEGORY_COLORS = ["#0c75e6", "#2fd5e4", "#f5a623", "#8d5cf6", "#ef5da8", "#2fb572", "#8d99ae"];

const VAT_RATE = 7; // ภาษีมูลค่าเพิ่ม %
const WHT_RATE = 3; // ภาษีหัก ณ ที่จ่าย %

const RUNWAY_MONTHS_LOOKBACK = 2; // จำนวนเดือนย้อนหลังที่ใช้หาค่าเฉลี่ยรายจ่าย (ไม่รวมเดือนปัจจุบัน)

// คีย์เดือนปัจจุบันรูปแบบ YYYY-MM (ใช้ตัดเดือนที่ยังไม่จบออกจากค่าเฉลี่ย)
const currentMonthKey = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
})();

// ภาษีเงินได้นิติบุคคล อัตรา SME (ทุนจดทะเบียนชำระแล้ว <= 5 ลบ. และรายได้ <= 30 ลบ./ปี)
// กำไรสุทธิ 0-300,000 ยกเว้น | 300,001-3,000,000 = 15% | ส่วนที่เกิน 3,000,000 = 20%
const CIT_BRACKETS = [
  { upTo: 300000, rate: 0 },
  { upTo: 3000000, rate: 0.15 },
  { upTo: Infinity, rate: 0.2 },
];

// อัตราขั้นสูงสุดที่กำไรก้อนนี้ไปแตะ (marginal rate)
const marginalCitRate = (netProfit) => {
  if (!netProfit || netProfit <= 0) return 0;
  const bracket = CIT_BRACKETS.find((b) => netProfit <= b.upTo) || CIT_BRACKETS[CIT_BRACKETS.length - 1];
  return bracket.rate;
};

const calcCorporateTax = (netProfit) => {
  if (!netProfit || netProfit <= 0) return 0;
  let tax = 0;
  let lower = 0;
  CIT_BRACKETS.forEach((b) => {
    const taxable = Math.min(netProfit, b.upTo) - lower;
    if (taxable > 0) tax += taxable * b.rate;
    lower = b.upTo;
  });
  return tax;
};

const defaultSummary = {
  total_count: 0,
  total_income: 0,
  total_income_net: 0,
  total_expense: 0,
  total_expense_net: 0,
  balance: 0,
  total_subtotal: 0,
  total_vat: 0,
  total_wht: 0,
  total_net: 0,
};

const formatMoney = (value) => {
  const num = Number(String(value ?? "").replace(/,/g, ""));
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCurrency = (value) => `฿ ${formatMoney(value)}`;

// ย่อยอดเงินสำหรับแกนกราฟ เช่น 1.2M / 45K
const formatCompact = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(num / 1_000)}K`;
  return String(num);
};

const getCategoryName = (item) =>
  item.category?.category_name || item.category?.category_code || item.category_name || "ไม่ระบุหมวดหมู่";

// ดึงปี-เดือน (0-11) จากค่า transaction_date หลายรูปแบบ
const getMonthIndex = (dateStr) => {
  if (!dateStr) return null;
  const part = String(dateStr).slice(5, 7);
  const month = parseInt(part, 10);
  return Number.isNaN(month) ? null : month - 1;
};

export default function FinanceSummary() {
  const currentYear = new Date().getFullYear();
  const [summary, setSummary] = useState(defaultSummary);
  const [transactions, setTransactions] = useState([]);
  const [year, setYear] = useState(String(currentYear));
  const [showRunway, setShowRunway] = useState(false);

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 4; y -= 1) {
      arr.push({ value: String(y), label: `ปี ${y}` });
    }
    return arr;
  }, [currentYear]);

  const filterBody = useMemo(
    () => ({
      data_search: "",
      status: "",
      date_from: `${year}-01-01`,
      date_to: `${year}-12-31`,
    }),
    [year]
  );

  const getSummary = useCallback(async () => {
    const result = await GET("v1/admin/finance-transactions/summary", filterBody);
    if (result?.status) {
      setSummary({ ...defaultSummary, ...(result.data || {}) });
    }
  }, [filterBody]);

  const getTransactions = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/finance-transactions", { page: 0, ...filterBody });
    if (result?.status) {
      setTransactions(result.data || []);
    }
    loading(false);
  }, [filterBody]);

  useEffect(() => {
    getSummary();
    getTransactions();
  }, [getSummary, getTransactions]);

  // คำนวณข้อมูลกราฟรายเดือน และค่าใช้จ่ายรายหมวด จากรายการธุรกรรมจริง
  const derived = useMemo(() => {
    const monthly = THAI_MONTHS.map((label) => ({ label, income: 0, expense: 0 }));
    const expenseByCat = new Map();

    transactions.forEach((item) => {
      const amount = Number(item.amount) || 0;
      const isIncome = item.transaction_type === "income";
      const monthIdx = getMonthIndex(item.transaction_date);
      if (monthIdx !== null && monthIdx >= 0 && monthIdx < 12) {
        if (isIncome) monthly[monthIdx].income += amount;
        else monthly[monthIdx].expense += amount;
      }
      if (!isIncome) {
        const catName = getCategoryName(item);
        const prev = expenseByCat.get(catName) || { amount: 0, count: 0 };
        expenseByCat.set(catName, { amount: prev.amount + amount, count: prev.count + 1 });
      }
    });

    const expenseList = Array.from(expenseByCat.entries())
      .map(([label, v]) => ({ label, amount: v.amount, count: v.count }))
      .sort((a, b) => b.amount - a.amount);

    const totalExpense = expenseList.reduce((sum, c) => sum + c.amount, 0);

    return { monthly, expenseList, totalExpense };
  }, [transactions]);

  // ค่าใช้จ่ายหลัก 5 อันดับ + รวมที่เหลือเป็น "อื่นๆ" บรรทัดเดียว
  const topExpenses = useMemo(() => {
    const TOP = 5;
    const top = derived.expenseList.slice(0, TOP);
    const rest = derived.expenseList.slice(TOP);
    if (rest.length) {
      top.push({
        label: `อื่นๆ อีก ${rest.length} หมวด`,
        amount: rest.reduce((sum, c) => sum + c.amount, 0),
        count: rest.reduce((sum, c) => sum + c.count, 0),
      });
    }
    return top;
  }, [derived.expenseList]);

  // ประมาณการว่าเงินคงเหลือจะใช้ได้อีกกี่เดือน
  // เทียบกับค่าเฉลี่ยรายจ่ายย้อนหลัง 2 เดือน และหักภาระภาษี/ยอดค้างจ่ายที่ต้องจ่ายจริงออกก่อน
  const runway = useMemo(() => {
    let incomeAmount = 0; // ยอดก่อน VAT ฝั่งรายรับ
    let expenseAmount = 0; // ยอดก่อน VAT ฝั่งรายจ่าย
    let incomeVat = 0; // VAT ขาย (เก็บจากลูกค้า)
    let expenseVat = 0; // VAT ซื้อ (จ่ายให้ผู้ขาย)
    let incomeWht = 0; // ถูกลูกค้าหัก ณ ที่จ่าย = เครดิตภาษีของเรา
    let expenseWht = 0; // เราหักผู้ขายไว้ = ต้องนำส่งสรรพากร
    let incomeNet = 0;
    let expenseNet = 0;
    const expenseByMonth = new Map(); // "YYYY-MM" -> ยอดจ่ายสุทธิ

    transactions.forEach((item) => {
      const amount = Number(item.amount) || 0;
      const vat = Number(item.vat) || 0;
      const wht = Number(item.wht) || 0;
      const net = Number(item.amount_net) || 0;
      if (item.transaction_type === "income") {
        incomeAmount += amount;
        incomeVat += vat;
        incomeWht += wht;
        incomeNet += net;
      } else if (item.transaction_type === "expense") {
        expenseAmount += amount;
        expenseVat += vat;
        expenseWht += wht;
        expenseNet += net;
        const key = String(item.transaction_date || "").slice(0, 7);
        if (key.length === 7) expenseByMonth.set(key, (expenseByMonth.get(key) || 0) + net);
      }
    });

    // ตัดเดือนปัจจุบันออก เพราะยังเก็บรายจ่ายไม่ครบเดือน จะทำให้ค่าเฉลี่ยต่ำเกินจริง
    const monthKeys = Array.from(expenseByMonth.keys())
      .filter((key) => key < currentMonthKey)
      .sort();
    const usedMonths = monthKeys.slice(-RUNWAY_MONTHS_LOOKBACK);
    const monthDetails = usedMonths.map((key) => {
      const [y, m] = key.split("-");
      return { key, label: `${THAI_MONTHS[Number(m) - 1] || key} ${y}`, amount: expenseByMonth.get(key) || 0 };
    });
    const avgMonthlyExpense = usedMonths.length ? monthDetails.reduce((s, m) => s + m.amount, 0) / usedMonths.length : 0;

    const cashBalance = incomeNet - expenseNet;
    const vatPayable = Math.max(0, incomeVat - expenseVat); // VAT ขาย - VAT ซื้อ ที่ต้องนำส่ง
    const whtPayable = expenseWht; // ภาษีหัก ณ ที่จ่ายที่เราหักไว้ ต้องนำส่ง
    const netProfit = incomeAmount - expenseAmount;
    const corporateTax = calcCorporateTax(netProfit);
    const whtCredit = Math.min(incomeWht, corporateTax); // เครดิตภาษีที่ถูกหักไว้ ใช้ลดภาษีนิติบุคคลได้
    const corporateTaxDue = Math.max(0, corporateTax - whtCredit);
    // อัตราภาษีที่เสียจริง = ภาษีทั้งก้อน / กำไรสุทธิ (ต่ำกว่าอัตราขั้นบันได เพราะ 300,000 แรกได้ยกเว้น)
    const effectiveTaxRate = netProfit > 0 ? (corporateTax / netProfit) * 100 : 0;
    const marginalTaxRate = marginalCitRate(netProfit) * 100;
    // อัตราที่จ่ายเพิ่มจริงหลังใช้เครดิต WHT
    const netTaxRate = netProfit > 0 ? (corporateTaxDue / netProfit) * 100 : 0;
    const totalDeduction = vatPayable + whtPayable + corporateTaxDue;
    const availableCash = cashBalance - totalDeduction;

    const monthsRaw = avgMonthlyExpense > 0 ? cashBalance / avgMonthlyExpense : null;
    const monthsNet = avgMonthlyExpense > 0 ? availableCash / avgMonthlyExpense : null;

    return {
      incomeAmount,
      expenseAmount,
      incomeVat,
      expenseVat,
      incomeWht,
      expenseWht,
      cashBalance,
      vatPayable,
      whtPayable,
      netProfit,
      corporateTax,
      whtCredit,
      corporateTaxDue,
      effectiveTaxRate,
      marginalTaxRate,
      netTaxRate,
      totalDeduction,
      availableCash,
      avgMonthlyExpense,
      monthDetails,
      monthsRaw,
      monthsNet,
    };
  }, [transactions]);

  const runwayText =
    runway.monthsNet === null
      ? "ไม่มีข้อมูลรายจ่าย"
      : runway.monthsNet <= 0
      ? "0 เดือน"
      : `${runway.monthsNet.toFixed(1)} เดือน`;

  // ตัวเลขที่ต้องรู้จริง ๆ 4 ตัว (รายละเอียดภาษีเต็ม ๆ อยู่ในป๊อปอัปประมาณการ)
  const statCards = [
    {
      label: "รายรับสุทธิ",
      value: formatCurrency(summary.total_income_net),
      hint: "รวม VAT และหัก ณ ที่จ่ายแล้ว",
      icon: "fa-arrow-trend-up",
      color: "#188251",
    },
    {
      label: "รายจ่ายสุทธิ",
      value: formatCurrency(summary.total_expense_net),
      hint: "รวม VAT และหัก ณ ที่จ่ายแล้ว",
      icon: "fa-arrow-trend-down",
      color: "#b63636",
    },
    {
      label: "กำไรก่อนภาษี",
      value: formatCurrency(runway.netProfit),
      hint: "รายรับ − รายจ่าย (ยอดก่อน VAT)",
      icon: "fa-chart-line",
      color: "#0c75e6",
    },
    {
      label: "ภาษีที่ต้องกันไว้",
      value: formatCurrency(runway.totalDeduction),
      hint: `VAT ${formatMoney(runway.vatPayable)} · WHT ${formatMoney(runway.whtPayable)} · นิติบุคคล ${formatMoney(runway.corporateTaxDue)}`,
      icon: "fa-file-invoice-dollar",
      color: "#8d5cf6",
    },
  ];

  const barData = {
    labels: derived.monthly.map((m) => m.label),
    datasets: [
      {
        label: "รายรับ",
        data: derived.monthly.map((m) => m.income),
        backgroundColor: "rgba(12, 117, 230, 0.85)",
        borderRadius: 8,
        maxBarThickness: 22,
      },
      {
        label: "รายจ่าย",
        data: derived.monthly.map((m) => m.expense),
        backgroundColor: "rgba(245, 87, 122, 0.85)",
        borderRadius: 8,
        maxBarThickness: 22,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { usePointStyle: true, padding: 16, boxWidth: 8 } },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ฿ ${formatMoney(ctx.parsed.y)}` },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#7d94a6" } },
      y: {
        border: { display: false },
        grid: { color: "rgba(15, 102, 182, 0.07)" },
        ticks: { color: "#7d94a6", callback: (value) => `฿ ${formatCompact(value)}` },
      },
    },
  };

  return (
    <AppShell title="สรุปยอดเงินบริษัท" description="ภาพรวมการเงินแบบย่อ: เงินคงเหลือ ใช้ได้อีกกี่เดือน และค่าใช้จ่ายหลักของปี">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <span className="badge rounded-pill px-3 py-2" style={{ background: "rgba(12, 117, 230, 0.10)", color: "#0c75e6", fontWeight: 600 }}>
          <i className="fa-solid fa-calendar-day me-2" />
          ข้อมูลประจำปี {year}
        </span>
        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary small">เลือกปี</span>
          <div className="flex-grow-1 flex-md-grow-0" style={{ width: 150 }}>
            <Select
              className="form-control"
              value={year}
              options={yearOptions}
              placeholder="เลือกปี"
              onChange={(value) => setYear(value)}
            />
          </div>
        </div>
      </div>

      {/* ตัวเลขเดียวที่ต้องรู้ก่อนเพื่อน: เหลือเท่าไร และอยู่ได้อีกกี่เดือน */}
      <div className="p-4 p-md-5 mb-4 text-white" style={heroCardStyle}>
        <div style={{ position: "absolute", top: -90, right: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,0.10)" }} />
        <div style={{ position: "absolute", bottom: -120, right: 120, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-7">
            <div className="text-uppercase small mb-2" style={{ letterSpacing: "0.16em", opacity: 0.78 }}>
              เงินคงเหลือ ปี {year}
            </div>
            <div className="fw-bold finsum-hero-value" style={{ fontSize: "clamp(2.1rem, 5vw, 3.2rem)", lineHeight: 1.08, letterSpacing: "-0.01em" }}>
              {formatCurrency(summary.balance)}
            </div>
            <div className="small mt-2" style={{ opacity: 0.82 }}>
              รายรับสุทธิ {formatCurrency(summary.total_income_net)} − รายจ่ายสุทธิ {formatCurrency(summary.total_expense_net)}
            </div>
          </div>

          <div className="col-lg-5">
            <button
              type="button"
              onClick={() => setShowRunway(true)}
              className="w-100 text-start border-0 p-3 p-md-4 text-white"
              style={{ borderRadius: 22, background: "rgba(255,255,255,0.16)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.28)" }}
              title="ดูวิธีคำนวณและรายการหักทั้งหมด"
            >
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div className="overflow-hidden">
                  <div className="small" style={{ opacity: 0.86 }}>หลังหักภาษีและภาระทั้งหมด ใช้ได้อีกประมาณ</div>
                  <div className="fw-bold" style={{ fontSize: "1.75rem", lineHeight: 1.2 }}>{runwayText}</div>
                  <div className="small text-truncate" style={{ opacity: 0.86 }}>
                    เงินใช้ได้จริง {formatCurrency(runway.availableCash)}
                  </div>
                </div>
                <i className="fa-solid fa-chevron-right flex-shrink-0" style={{ opacity: 0.8 }} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ตัวเลขประกอบ 4 ตัว */}
      <div className="row g-3 mb-4">
        {statCards.map((card) => (
          <div key={card.label} className="col-12 col-md-6 col-xl-3">
            <div className="h-100 p-3 p-md-4 d-flex flex-column" style={{ ...statCardStyle, boxShadow: `${statCardStyle.boxShadow}, inset 3px 0 0 ${card.color}` }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="small text-uppercase" style={{ letterSpacing: "0.1em", color: "#6e8c9c", fontWeight: 700, fontSize: "0.7rem" }}>
                  {card.label}
                </span>
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 34, height: 34, borderRadius: 12, background: `${card.color}1a`, color: card.color }}
                >
                  <i className={`fa-solid ${card.icon}`} style={{ fontSize: "0.85rem" }} />
                </span>
              </div>
              <div className="fw-bold" style={{ color: card.color, fontSize: "1.35rem", fontVariantNumeric: "tabular-nums" }}>
                {card.value}
              </div>
              <div className="small mt-1 text-truncate" style={{ color: "#8fa6b6", fontSize: "0.74rem" }} title={card.hint}>
                {card.hint}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-7">
          <div className="p-3 p-md-4 h-100" style={statCardStyle}>
            <h5 className="fw-semibold mb-1">รายรับ vs รายจ่าย รายเดือน</h5>
            <p className="text-secondary mb-3 small">ยอดก่อนภาษีของแต่ละเดือนในปี {year}</p>
            <div style={{ minHeight: 320 }}>
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="p-3 p-md-4 h-100" style={statCardStyle}>
            <h5 className="fw-semibold mb-1">ค่าใช้จ่ายหลัก</h5>
            <p className="text-secondary mb-3 small">หมวดที่ใช้เงินมากที่สุดของปี {year}</p>

            {topExpenses.length > 0 ? (
              topExpenses.map((item, index) => {
                const pct = derived.totalExpense > 0 ? (item.amount / derived.totalExpense) * 100 : 0;
                const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                return (
                  <div key={item.label} className="mb-3">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span className="fw-semibold text-truncate" style={{ color: "#22384a", fontSize: "0.92rem" }} title={item.label}>
                        {item.label}
                      </span>
                      <span className="fw-bold flex-shrink-0" style={{ color: "#22384a", fontVariantNumeric: "tabular-nums", fontSize: "0.92rem" }}>
                        {formatMoney(item.amount)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <div className="flex-grow-1" style={{ height: 6, borderRadius: 999, background: "rgba(15, 53, 81, 0.07)" }}>
                        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: color }} />
                      </div>
                      <span className="small flex-shrink-0" style={{ color: "#8fa6b6", fontSize: "0.72rem", minWidth: 34, textAlign: "right" }}>
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-secondary small py-5">ยังไม่มีข้อมูลค่าใช้จ่าย</div>
            )}
          </div>
        </div>
      </div>

      <div className="text-center small mt-4 mb-2" style={{ color: "#8fa6b6" }}>
        ดูรายการธุรกรรมทั้งหมดได้ที่เมนู "ธุรกรรมการเงินบริษัท"
      </div>

      <Modal show={showRunway} onHide={() => setShowRunway(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="fw-semibold">
            <i className="fa-solid fa-hourglass-half me-2" style={{ color: "#2fb572" }} />
            ประมาณการเงินคงเหลือใช้ได้อีกกี่เดือน
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="rounded-4 p-3 mb-3 text-white text-center" style={{ background: "linear-gradient(135deg, #0b6b3a 0%, #2fb572 100%)" }}>
            <div className="small" style={{ opacity: 0.9 }}>หลังหักภาษีและภาระที่ต้องจ่ายแล้ว ใช้ได้อีกประมาณ</div>
            <div className="fw-bold" style={{ fontSize: "2rem" }}>{runwayText}</div>
            <div className="small" style={{ opacity: 0.9 }}>
              เงินใช้ได้จริง {formatCurrency(runway.availableCash)} ÷ ค่าเฉลี่ยรายจ่าย {formatCurrency(runway.avgMonthlyExpense)}/เดือน
            </div>
          </div>

          {/* 1. ฐานค่าเฉลี่ยรายจ่าย */}
          <h6 className="fw-semibold mb-2">1. ค่าเฉลี่ยรายจ่าย {RUNWAY_MONTHS_LOOKBACK} เดือนก่อนหน้า (ไม่รวมเดือนปัจจุบัน)</h6>
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle mb-0 finsum-modal-table">
              <tbody>
                {runway.monthDetails.length > 0 ? (
                  runway.monthDetails.map((m) => (
                    <tr key={m.key}>
                      <td>รายจ่ายเดือน {m.label}</td>
                      <td className="text-end" style={{ color: "#b63636" }}>{formatCurrency(m.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td className="text-secondary">ยังไม่มีข้อมูลรายจ่ายของเดือนก่อนหน้าในปี {year}</td><td /></tr>
                )}
                <tr className="fw-semibold" style={{ borderTop: "2px solid #e6edf5" }}>
                  <td>ค่าเฉลี่ยต่อเดือน (ใช้ {runway.monthDetails.length} เดือนล่าสุดที่มีข้อมูล)</td>
                  <td className="text-end">{formatCurrency(runway.avgMonthlyExpense)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 2. รายการหัก */}
          <h6 className="fw-semibold mb-2">2. ภาระที่ต้องหักออกจากเงินคงเหลือ</h6>
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle mb-0 finsum-modal-table">
              <thead className="table-light">
                <tr>
                  <th className="text-secondary fw-medium">รายการ</th>
                  <th className="text-secondary fw-medium text-end finsum-amt-col" style={{ width: 170 }}>จำนวนเงิน</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    เงินคงเหลือ (รายรับสุทธิ − รายจ่ายสุทธิ)
                    <div className="text-secondary small">{formatCurrency(summary.total_income_net)} − {formatCurrency(summary.total_expense_net)}</div>
                  </td>
                  <td className="text-end fw-semibold">{formatCurrency(runway.cashBalance)}</td>
                </tr>
                <tr>
                  <td>
                    หัก ภาษีมูลค่าเพิ่มค้างนำส่ง (VAT {VAT_RATE}%)
                    <div className="text-secondary small">VAT ขาย {formatCurrency(runway.incomeVat)} − VAT ซื้อ {formatCurrency(runway.expenseVat)}</div>
                  </td>
                  <td className="text-end" style={{ color: "#b63636" }}>-{formatMoney(runway.vatPayable)}</td>
                </tr>
                <tr>
                  <td>
                    หัก ภาษีหัก ณ ที่จ่ายที่ต้องนำส่ง (WHT {WHT_RATE}%)
                    <div className="text-secondary small">ยอดที่หักไว้จากผู้ขาย/ผู้รับจ้าง ยังไม่ใช่เงินของบริษัท</div>
                  </td>
                  <td className="text-end" style={{ color: "#b63636" }}>-{formatMoney(runway.whtPayable)}</td>
                </tr>
                <tr>
                  <td>
                    หัก ภาษีเงินได้นิติบุคคล
                    <span className="badge rounded-pill ms-2" style={{ background: "rgba(182,54,54,0.12)", color: "#b63636" }}>
                      เสียจริง {runway.effectiveTaxRate.toFixed(2)}%
                    </span>
                    <div className="text-secondary small">
                      กำไรสุทธิ (ก่อน VAT) {formatCurrency(runway.netProfit)} → ภาษี {formatCurrency(runway.corporateTax)}
                      {runway.whtCredit > 0 ? ` − เครดิต WHT ที่ถูกหักไว้ ${formatCurrency(runway.whtCredit)}` : ""}
                    </div>
                    <div className="text-secondary small">
                      อัตราขั้นบันไดที่แตะ {runway.marginalTaxRate.toFixed(0)}% · เสียจริงเฉลี่ย {runway.effectiveTaxRate.toFixed(2)}% ของกำไรสุทธิ
                      {runway.whtCredit > 0 ? ` · จ่ายเพิ่มจริงหลังหักเครดิต ${runway.netTaxRate.toFixed(2)}%` : ""}
                    </div>
                  </td>
                  <td className="text-end" style={{ color: "#b63636" }}>-{formatMoney(runway.corporateTaxDue)}</td>
                </tr>
                <tr className="fw-semibold" style={{ borderTop: "2px solid #e6edf5" }}>
                  <td>รวมรายการหัก</td>
                  <td className="text-end" style={{ color: "#b63636" }}>-{formatMoney(runway.totalDeduction)}</td>
                </tr>
                <tr className="fw-bold">
                  <td>เงินที่ใช้ได้จริง</td>
                  <td className="text-end" style={{ color: runway.availableCash >= 0 ? "#188251" : "#b63636" }}>{formatCurrency(runway.availableCash)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 3. ผลลัพธ์ */}
          <h6 className="fw-semibold mb-2">3. ผลลัพธ์</h6>
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle mb-0 finsum-modal-table">
              <tbody>
                <tr>
                  <td>ถ้าไม่หักภาษีเลย (ใช้ยอดคงเหลือดิบ)</td>
                  <td className="text-end fw-semibold">{runway.monthsRaw === null ? "-" : `${Math.max(0, runway.monthsRaw).toFixed(1)} เดือน`}</td>
                </tr>
                <tr>
                  <td className="fw-semibold">หลังหักภาษีและภาระทั้งหมด</td>
                  <td className="text-end fw-bold" style={{ color: "#188251" }}>{runwayText}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="rounded-3 p-3 small text-secondary" style={{ background: "rgba(12,117,230,0.06)" }}>
            <div className="fw-semibold mb-1" style={{ color: "#0c75e6" }}>สมมติฐานที่ใช้</div>
            <ul className="mb-0 ps-3">
              <li>ค่าเฉลี่ยรายจ่ายคิดจาก {RUNWAY_MONTHS_LOOKBACK} เดือนก่อนหน้าที่มีรายการจ่าย ไม่รวมเดือนปัจจุบัน (เดือนนี้ยังเก็บรายจ่ายไม่ครบ)</li>
              <li>ใช้ยอดสุทธิ (ยอดรวม + VAT − หัก ณ ที่จ่าย) ตามที่บันทึกในธุรกรรม</li>
              <li>ภาษีเงินได้นิติบุคคลใช้อัตรา SME: กำไร ≤ 300,000 ยกเว้น / 300,001-3,000,000 = 15% / ส่วนเกิน 3,000,000 = 20%</li>
              <li>ยังไม่รวมรายรับในอนาคต ค่าเสื่อมราคา และรายจ่ายผูกพันที่ยังไม่ได้บันทึกเป็นธุรกรรม</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowRunway(false)}>ปิด</button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
}
